import {action_ids, eActionType, getActionId} from '@common/actions/action-types';
import {ActionShared} from '@common/actions/action-version-implementation/action-shared';
import {eActionTemplate} from '@common/request_enums';
import {TranslateService} from '@ngx-translate/core';
import {EActionsVersions} from '@common/actions/actions-version';

export interface IActionsValueItem {
    name: string;
    value: string;
}

export interface IPreparedAction {
    template_id: eActionTemplate;
    action_id: action_ids;
    values: IActionsValueItem[];
}

export class ActionVersionBase {
    private version: EActionsVersions;
    private type: eActionType;
    private translate: TranslateService;

    constructor(version: EActionsVersions, type: eActionType, translate: TranslateService) {
        this.version = version;
        this.type = type;
        this.translate = translate;
    }

    public getPreparedAction(): IPreparedAction
    {
        const templateID = ActionShared.getTemplate(this.type);
        const action = actions[this.type];
        return {
            template_id: templateID,
            ...action,
        };
    }

    public async extractActionValues(info: IPreparedAction, ...args: string[]): Promise<IActionsValueItem[]>
    {
        return await Promise.all(info.values.map(async (kv) =>
        {
            const nam = await ActionShared.extractValue(kv.name, this.translate, ...args);
            const val = await ActionShared.extractValue(kv.value, this.translate, ...args);
            return { name: nam, value: val };
        }));
    }
}

const actions = {
    [ eActionType.SignMessage ]: {
        action_id: `${ getActionId(eActionType.SignMessage) }:${ Date.now() }`,
        values: [
            {
                name: 'Sign message\n', value: '\n{0}'
            }
        ]
    },
    [ eActionType.TransactionStart ]: {
        action_id: `${ getActionId(eActionType.TransactionStart) }:{0}`,
        values: [
            { name: 'ACTIONS.TRANSACTION.SRC-TITLE', value: '{1}' },
            { name: 'ACTIONS.TRANSACTION.DST-TITLE', value: '{2}' },
            { name: '{3}', value: '{4}' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.ExchangeStart ]: {
        action_id: `${ getActionId(eActionType.ExchangeStart) }:{0}`,
        values: [
            { name: '{1}', value: 'ACTIONS.EXCHANGE.LABEL' },
            { name: 'ACTIONS.EXCHANGE.PAY-TITLE', value: '{2} {3}' },
            { name: 'ACTIONS.EXCHANGE.GET-TITLE', value: '{4} {5}' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.ExchangeLoad ]: {
        action_id: `${ getActionId(eActionType.ExchangeLoad) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.EXCHANGE-TITLE', value: '{1}' },
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.EXCHANGE-LOAD' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.BackupRemove ]: {
        action_id: `${ getActionId(eActionType.BackupRemove) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.WALLET-TITLE', value: '{1}' },
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.BACKUP-REMOVE' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.BackupLoad ]: {
        action_id: `${ getActionId(eActionType.BackupLoad) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.WALLET-TITLE', value: '{1}' },
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.BACKUP-LOAD' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.ExportPhrase ]: {
        action_id: `${ getActionId(eActionType.ExportPhrase) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.WALLET-TITLE', value: '{1}' },
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.EXPORT-PHRASE' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.ExportJson ]: {
        action_id: `${ getActionId(eActionType.ExportJson) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.WALLET-TITLE', value: '{1}' },
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.EXPORT-JSON' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.ExportKey ]: {
        action_id: `${ getActionId(eActionType.ExportKey) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.WALLET-TITLE', value: '{1}' },
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.EXPORT-KEY' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.WalletRemove ]: {
        action_id: `${ getActionId(eActionType.WalletRemove) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.WALLET-TITLE', value: '{1}' },
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.WALLET-REMOVE' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.WalletUpdate ]: {
        action_id: `${ getActionId(eActionType.WalletUpdate) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.WALLET-TITLE', value: '{1}' },
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.WALLET-UPDATE' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.SecurityChange ]: {
        action_id: `${ getActionId(eActionType.SecurityChange) }`,
        values: [
            { name: 'ACTIONS.ACTION.SECURITY-CHANGE', value: '' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.AccountRemove ]: {
        action_id: `${ getActionId(eActionType.AccountRemove) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.ACCOUNT-REMOVE' },
            { name: '--', value: '' }
        ]
    },
    [ eActionType.AddAddress ]: {
        action_id: `${ getActionId(eActionType.AddAddress) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.ADD-ADDRESS' },
            { name: 'ACTIONS.ACTION.WALLET-TITLE', value: '{1}' },
            { name: '--', value: '' }
        ]
    },
};
